import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { ArrowDirection, IAttachedGalleryFile } from "modules/gallery/models";
import { DialogActions } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import InlineGalleryItem from "../inlineGalleryPreviewItem";
import { IPosition } from "modules/common/hooks/useDragReorder";
import ExpandingDiv from "../../expandingDiv";
import { useImageArrow } from "modules/common/hooks/useImageArrow";

import "../styles/imageGalleryDialog.sass";
import '../styles/inlineGallery.sass';
import ImageCaptionInput from "../imageCaptionInput";

const ImageGalleryArrangementDialog: React.FunctionComponent<IConnectedImageGalleryArrangementDialogProps> = props => {
    const { show, selectedImages } = props;

    const [focusedImage, setFocusedImage] = React.useState(selectedImages[0]);
    const [captionText, setCaptionText] = React.useState(focusedImage?.caption ?? "");

    const draggedImage = props.draggedIdx !== null ? selectedImages[props.draggedIdx] : null;
    const draggedImageDimensions = document.getElementById("dragged-image")?.getClientRects();

    //Will need to offset the button placement.
    const draggedImageWidth = draggedImageDimensions ? draggedImageDimensions[0].width : 0;

    const setNewFocusedImage = (image: IAttachedGalleryFile) => {
        setFocusedImage(image);
        setCaptionText(image?.caption ?? "")
    }

    React.useEffect(() => {
        if (focusedImage === undefined && selectedImages.length > 0)
            setNewFocusedImage(selectedImages[0]);
    }, [selectedImages, focusedImage]);

    //When the dialog is opened, reset to default selection.
    React.useEffect(() => {
        if(show === true) {
            setNewFocusedImage(selectedImages[0]);
        }
    }, [show, selectedImages]);

    const onDeleteImage = (image: IAttachedGalleryFile) => {
        props.onRemoveImage(image);
        if (focusedImage.blobId === image.blobId) {
            setNewFocusedImage(selectedImages[0]);
        }
    };

    const selectAndScrollToImage = (imageToSet: IAttachedGalleryFile) => {
        setNewFocusedImage(imageToSet);

        const elementToScrollTo = document.getElementById(imageToSet.blobId);
        const verticalElement = document.getElementsByClassName(`inline-gallery-content`)[0];

        //We store and restore y height so that the scroll is purely horizontal.
        if(!!elementToScrollTo && !!verticalElement) {
            const y = verticalElement.scrollTop;

            elementToScrollTo.scrollIntoView();
            verticalElement.scrollTop = y;
        }
    }

    const onChangeCaption = () => {
        focusedImage.caption = captionText;
    }

    const onImageArrowClicked = useImageArrow({selectedImageDetails: selectedImages, focusedImage, selectAndScrollToImage});

    const getArrowIcon = (icon: React.JSX.Element, arrowDirection: ArrowDirection) => {
        return (
            <IconButton
                onClick={() => onImageArrowClicked(arrowDirection)}
                size="small"
                edge="end"
                className={`arrow-button-gallery ${arrowDirection}`}
                data-testid={`image-gallery-arrow-${arrowDirection}`}
            >
                {icon}
            </IconButton>
        );
    }

    return (
        <Dialog 
            open={show} 
            maxWidth={false} 
            onClose={props.onClose} 
            PaperProps={{
                sx: {
                    width: '900px', 
                }
            }} 
        >
            <DialogTitle className="events-dialog-header">
                <div className="events-dialog-header-title">
                    <div>Arrange Images</div>
                    <IconButton onClick={props.onClose} size="large" edge="end">
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent className="inline-gallery-content">
                <img
                    id={`dragged-image`}
                    alt={draggedImage ? "Currently being dragged." : ""}
                    style={{
                        left: props.mousePos.x - draggedImageWidth + 8,
                        top: props.mousePos.y - 6,
                    }}
                    className={`dragged-image ${draggedImage && "visible"}`}
                    draggable={false}
                    src={draggedImage?.fileUrl ?? ""}
                />
                <div>
                    <Button 
                        startIcon={
                            <AddIcon className="add-icon" />
                        }
                        onClick={props.onBackToImageEdit}
                    >
                        Add/Edit Images
                    </Button>
                    <div className="primary-image-container">
                        <img className="primary-image" src={focusedImage?.originalFileUrl ?? ""} alt={"Primary focused."}/>

                        {getArrowIcon(<KeyboardArrowLeftOutlinedIcon className="arrow-icon"/>, ArrowDirection.Left)}
                        {getArrowIcon(<KeyboardArrowRightOutlinedIcon className="arrow-icon"/>, ArrowDirection.Right)}
                    </div> 

                    <ImageCaptionInput 
                        captionText={captionText}
                        setCaptionText={setCaptionText}
                        activeLanguage={props.activeLanguage}
                        onBlur={onChangeCaption}
                    />

                    <div className="secondary-images-container">
                        <div className="secondary-images-wrapper">
                            <ExpandingDiv visible={(props.isDragging && props.dropIdx === 0)} startWidth={0} targetWidth={100}/>
                            {selectedImages.map((image, index) => {
                                if(index !== props.draggedIdx) {
                                    return <React.Fragment key={index}>
                                        <InlineGalleryItem 
                                            image={image} 
                                            isFirst={index === 0} 
                                            isLast={index === selectedImages.length - 1}
                                            onSelectImage={selectAndScrollToImage}
                                            onRemoveImage={onDeleteImage}
                                            onSwapForwards={() => {
                                                props.onSwapImages(image, selectedImages[index+1] ?? null);
                                            }}
                                            onSwapBackwards={() => {
                                                props.onSwapImages(image, selectedImages[index-1] ?? null);
                                            }}
                                            index={index}
                                            onDrag={props.onDrag}
                                            dropClass={props.dropClass}
                                        />
                                        <ExpandingDiv visible={(props.isDragging && props.dropIdx === index + 1)} startWidth={0} targetWidth={100}/>
                                    </React.Fragment>
                                }
                                else {
                                    return null;
                                }
                            })}
                            <div key={"placeholder"} className={props.dropClass}/>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions
                className="inline-gallery-footer"
            >
                <Button onClick={props.onClose} className="cancel">
                    Cancel
                </Button>
                <Button color="primary" variant="contained" onClick={props.onInsertImages}>
                    Finish
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface IImageGalleryArrangementDialogProps {
    show: boolean;
    selectedImages: IAttachedGalleryFile[];
    dropClass: string;
    draggedIdx: number | null;
    isDragging: boolean;
    mousePos: IPosition;
    dropIdx: number;
    activeLanguage: string;

    onClose: () => void;
    onBackToImageEdit: () => void;
    onInsertImages: () => void;
    onRemoveImage: (image: IAttachedGalleryFile) => void;
    onSwapImages: (imageOne: IAttachedGalleryFile, imageTwo: IAttachedGalleryFile) => void;
    onDrag: (idx: number, id: string) => void;
}

const connector = connect((state: GlobalApplicationState, ownProps: IImageGalleryArrangementDialogProps) => ({ 
        ...ownProps, 
    })
);
type IConnectedImageGalleryArrangementDialogProps = ConnectedProps<typeof connector>;

export default connector(ImageGalleryArrangementDialog);